const MASK64 = (1n << 64n) - 1n;
const MULTIPLIER = 6364136223846793005n;

/** PCG32 (XSH-RR) reference generator, bit-identical to the C#/Go implementations. */
export class Pcg32 {
  private state: bigint;
  private readonly inc: bigint;

  constructor(seed: bigint | number, sequence: bigint | number = 54n) {
    this.inc = ((BigInt(sequence) << 1n) | 1n) & MASK64;
    this.state = 0n;
    this.step();
    this.state = (this.state + BigInt(seed)) & MASK64;
    this.step();
  }

  get stateValue(): bigint { return this.state; }

  /** A copy at the same point of the same stream: a look-ahead draws from it without moving this one (sim/scout.ts). */
  clone(): Pcg32 {
    const c = Object.create(Pcg32.prototype) as Pcg32;
    (c as unknown as { state: bigint; inc: bigint }).state = this.state;
    (c as unknown as { state: bigint; inc: bigint }).inc = this.inc;
    return c;
  }

  private step(): void {
    this.state = (this.state * MULTIPLIER + this.inc) & MASK64;
  }

  /** Uniform 32-bit unsigned integer as a JS number. */
  nextUInt(): number {
    const old = this.state;
    this.step();
    const xorShifted = Number((((old >> 18n) ^ old) >> 27n) & 0xffffffffn);
    const rot = Number(old >> 59n);
    return ((xorShifted >>> rot) | (xorShifted << ((-rot) & 31))) >>> 0;
  }

  /** Unbiased integer in [0, maxExclusive). */
  nextInt(maxExclusive: number): number {
    if (maxExclusive <= 0) throw new RangeError("Pcg32.nextInt: bound must be positive");
    const bound = maxExclusive >>> 0;
    const threshold = (0x100000000 - bound) % bound;
    for (;;) {
      const r = this.nextUInt();
      if (r >= threshold) return r % bound;
    }
  }

  /** Uniform Q32.32 raw value in [0, 1). */
  nextUnitRaw(): bigint { return BigInt(this.nextUInt()); }

  nextSeed(): bigint { return (BigInt(this.nextUInt()) << 32n) | BigInt(this.nextUInt()); }
}
